import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticationService } from '../_services/authentication.service';
import { ErrorCode, ErrorObject } from '../_models/error';
import { AuthEventService } from '../_services/auth-event.service';

export const errorInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authenticationService = inject(AuthenticationService);
  const authEventService = inject(AuthEventService);
  const router = inject(Router);

  return next(request).pipe(
    catchError((err: HttpErrorResponse) => {
      const errorBody = err.error as ErrorObject | null | undefined;
      const errorMessage: ErrorObject = {
        status: err.status,
        message: '',
        code: errorBody?.code ?? null,
      };
      // Client side error
      if (
        err.error instanceof ErrorEvent ||
        err.error instanceof ProgressEvent
      ) {
        errorMessage.message = 'Check your network settings';
      } else {
        // server side error
        errorMessage.message =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (err.error?.errors
            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              (err.error.title as string)
            : (errorBody?.message ?? (err.error as string))) ||
          'Something went wrong';
        const isLogoutRequest = request.url.endsWith('/logout');
        const isIntercomError = new URL(request.url, location.origin).pathname.startsWith('/intercom/');

        if (!isLogoutRequest && !isIntercomError && err.status === 403) {
          switch (errorMessage.code) {
            case ErrorCode.EmailConfirmationRequired:
              void router.navigate(['/awaiting-confirmation']);
              return EMPTY;
            case ErrorCode.TwoFactorRequired:
              void router.navigate(['/2fa']);
              return EMPTY;
            case ErrorCode.TwoFactorSetupRequired:
              authenticationService.patchCurrentUser({
                twoFactorSetupRequired: true,
              });
              void router.navigate(['/2fa-enrollment']);
              return EMPTY;
            case ErrorCode.TwoFactorSetupRequiredForAdminAction: {
              const orgId = authenticationService.currentOrganisation;
              const redirect = orgId
                ? `/organisations/${orgId}/details`
                : '/organisations';
              sessionStorage.setItem('postEnrollmentRedirect', redirect);
              authenticationService.patchCurrentUser({
                twoFactorSetupRequired: true,
              });
              void router.navigate(['/2fa-enrollment']);
              return EMPTY;
            }
            case ErrorCode.ForbiddenDataAccess:
            default:
              void router.navigate(['/403']);
              return EMPTY;
          }
        }

        if (!isLogoutRequest && err.status === 401) {
          switch (errorMessage.code) {
            case ErrorCode.TwoFactorChallengeExpired:
            case ErrorCode.TwoFactorInvalidCode:
            case ErrorCode.TwoFactorAlreadyEnabled:
              return throwError(() => errorMessage);
            default:
              authEventService.setUnauthorizedNavigationPending(true);
              void authenticationService.logout();
              void router.navigate(['/login'], {
                queryParams: { returnUrl: router.url },
              });
          }
        }

        if (err.status === 500) {
          switch (errorMessage.code) {
            case ErrorCode.RecoveryCodeGenerationFailed:
            case ErrorCode.TwoFactorEnableFailed:
              return throwError(() => errorMessage);
          }
        }

        if ([504].indexOf(err.status) !== -1) {
          errorMessage.message =
            'Check your network settings. If the error persists, please contact help@ogibiotec.com for support.';
        }
      }
      return throwError(() => errorMessage);
    }),
  );
};
